"use client"

import { TagsMetadataSection, TextMetadataSection } from "@/components"
import { PastClientsSection } from "@/components/MetadataSections/PastClientsSection"
import { FragmentType, graphql, useFragment } from "@/gql"
import React, { FC } from "react"
import { PortfolioUIState } from "../page"
import { PortfolioEditModal, useEditPortfolio } from "./edit/useEditPortfolio"

const AboutLeftContentFragment = graphql(`
  fragment AboutLeftContent on IndividualCreative {
    __typename
    id
    bio
    skills {
      edges {
        node {
          id
          name
        }
      }
    }
    creativeWorkTypes {
      edges {
        node {
          id
          name
          pluralizedName
        }
      }
    }
    languages {
      edges {
        node {
          id
          name
        }
      }
    }
    portfolioClients {
      edges {
        node {
          id
          avatarInitials
          name
          avatar48w: avatarUrl(width: 48)
        }
      }
    }
    ...PastClientsSection
  }
`)

interface Props {
  uiState: PortfolioUIState
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
  creative: FragmentType<typeof AboutLeftContentFragment> | null | undefined
}

export const AboutLeftContent: FC<Props> = ({ editProfileMethods, ...props }) => {
  const creative = useFragment(AboutLeftContentFragment, props.creative)

  const projectTypes = creative?.creativeWorkTypes?.edges

  const onClickBioEdit = () => {
    editProfileMethods?.setOpenModal(PortfolioEditModal.EditDescription)
  }

  const onClickSkillsEdit = () => {
    editProfileMethods?.setOpenModal(PortfolioEditModal.EditSkills)
  }

  const onClickProjectTypesEdit = () => {
    editProfileMethods?.setOpenModal(PortfolioEditModal.EditProjecTypes)
  }

  const onClickLanguageEdit = () => {
    editProfileMethods?.setOpenModal(PortfolioEditModal.EditLanguages)
  }

  return (
    <div className="flex flex-col gap-9">
      {(creative?.bio || editProfileMethods?.isEditing) && (
        <>
          <TextMetadataSection
            title="Bio"
            text={creative?.bio || "Add description"}
            isEditing={editProfileMethods?.isEditing}
            onEditClick={onClickBioEdit}
          />
        </>
      )}
      <PastClientsSection creative={creative} />
      {((!!creative?.skills?.edges && creative?.skills?.edges.length > 0) || editProfileMethods?.isEditing) && (
        <>
          <TagsMetadataSection
            title="Skills"
            tags={creative?.skills?.edges.map(edge => edge?.node?.name || "") ?? []}
            isEditing={editProfileMethods?.isEditing}
            onEditClick={onClickSkillsEdit}
          />
        </>
      )}
      {((!!projectTypes && projectTypes?.length > 0) || editProfileMethods?.isEditing) && (
        <>
          <TagsMetadataSection
            title="Project types"
            tags={projectTypes?.map(edge => edge?.node?.pluralizedName || "") ?? []}
            isEditing={editProfileMethods?.isEditing}
            onEditClick={onClickProjectTypesEdit}
          />
        </>
      )}
      <TagsMetadataSection
        title="Languages"
        tags={creative?.languages?.edges.map(edge => edge?.node?.name || "") ?? []}
        isEditing={editProfileMethods?.isEditing}
        onEditClick={onClickLanguageEdit}
      />
    </div>
  )
}
